import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { handleQuerySuccess } from '../../../../hooks/useQuriesSuccess';
import { apiRequest } from '../../../../Services/Common/ApiRequest';
import Global from '../../../../Services/Common/Global';
import useSaveMutation from '../../../../hooks/apis/useSaveMutaion';
import useDeleteMutation from '../../../../hooks/apis/useDeleteMutaion';
import { mapParamsBySchema, mapRowsBySchema } from '../../../../util';
import { COU_BASIC_Schema } from './COU_BASIC_Schema';

/** API 배치/단건 응답 → useSaveN용 { isSuccess, message }[] */
export const normalizeCouBasicBatchResults = (res) => {
  let list = [];
  if (Array.isArray(res)) list = res;
  else if (Array.isArray(res?.data)) list = res.data;
  else if (Array.isArray(res?.Data)) list = res.Data;
  else if (res && typeof res === 'object') {
    if ('isSuccess' in res || 'IsSuccess' in res) {
      list = [{ ...res }];
    }
  }
  return list.map((item) => ({
    ...item,
    isSuccess: item.isSuccess ?? item.IsSuccess ?? false,
    message:
      item.message ?? item.Message ?? item.errorMsg ?? item.ErrorMsg ?? '',
  }));
};

export const normalizeCouBasicR1Row = (r, rowIndex = 0) => {
  const coCd = r.CO_CD ?? r.coCd ?? '';
  const coNm = r.CO_NM ?? r.coNm ?? '';
  const plType = r.PL_TYPE ?? r.plType ?? '';
  const fallbackNo = `cou:${coCd}:${plType}:${rowIndex}`;
  return {
    no: r.no ?? r.No ?? r.Seq ?? r.seq ?? fallbackNo,
    CO_CD: coCd,
    CO_NM: coNm,
    PL_TYPE: plType,
  };
};

/** 회사별 메뉴 목록 GetCouBasicR1 */
export const useSearch1 = (options = {}) => {
  const { onSuccess, coCd, onErrorRow } = options;

  const query = useQuery({
    queryKey: ['CompanyMenu', 'getCouBasicR1', 'COU_BASIC', coCd],
    queryFn: () => {
      const params = mapParamsBySchema(COU_BASIC_Schema.mapper.search1, {
        coCd,
      });
      return apiRequest(
        'post',
        Global.urls.CompanyMenu.getCouBasicR1,
        params
      );
    },
    enabled: String(coCd ?? '').length > 0,
  });

  useEffect(() => {
    if (!query.isSuccess) return;
    const raw = query.data ?? {};
    if (raw.isSuccess === false || raw.IsSuccess === false) {
      handleQuerySuccess({ data: [] }, { onSuccess });
      return;
    }
    const arr = Array.isArray(raw.data)
      ? raw.data
      : Array.isArray(raw.Data)
        ? raw.Data
        : [];
    if (arr.length > 0 && arr[0]?.type === 'Error') {
      onErrorRow?.(arr[0]);
      handleQuerySuccess({ data: [] }, { onSuccess });
      return;
    }
    const mapped = arr.map((row, idx) => normalizeCouBasicR1Row(row, idx));
    handleQuerySuccess({ data: mapped }, { onSuccess });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- query 성공 시에만 onSuccess 호출
  }, [query.isSuccess, query.dataUpdatedAt]);

  return query;
};

/** 구분(PL_TYPE) 목록 GetCouBasicR2 */
export const useSearch2 = (options = {}) => {
  const { onSuccess, onEmpty, onApiError } = options;

  const query = useQuery({
    queryKey: ['CompanyMenu', 'getCouBasicR2', 'COU_BASIC'],
    queryFn: () =>
      apiRequest('post', Global.urls.CompanyMenu.getCouBasicR2, {}),
    enabled: true,
  });

  useEffect(() => {
    if (!query.isSuccess) return;
    const raw = query.data ?? {};
    if (raw.isSuccess === false || raw.IsSuccess === false) {
      onSuccess?.([]);
      return;
    }
    const arr = Array.isArray(raw.data)
      ? raw.data
      : Array.isArray(raw.Data)
        ? raw.Data
        : [];
    if (arr.length === 0) {
      onEmpty?.();
      onSuccess?.([]);
      return;
    }
    if (arr[0]?.type === 'Error') {
      onApiError?.(arr[0]);
      onSuccess?.([]);
      return;
    }
    const plList = arr
      .map((item) => ({
        name: item.PL_TYPE_NM ?? item.plTypeNm ?? '',
        value: item.PL_TYPE ?? item.plType ?? '',
      }))
      .filter((x) => String(x.value ?? '').trim() !== '');
    onSuccess?.(plList);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- query 성공 시에만 콜백 호출
  }, [query.isSuccess, query.dataUpdatedAt]);

  return query;
};

export const useSave1 = (context = {}, options = {}) => {
  return useSaveMutation(async (rows) => {
    const payload = mapRowsBySchema(rows, COU_BASIC_Schema.mapper.save1, context);
    const res = await apiRequest(
      'post1',
      Global.urls.CompanyMenu.saveCouBasicU1,
      payload
    );
    return normalizeCouBasicBatchResults(res);
  }, options);
};

export const useDelete1 = (options = {}) => {
  return useDeleteMutation(async (rows) => {
    const payload = mapRowsBySchema(rows, COU_BASIC_Schema.mapper.delete1);
    const res = await apiRequest(
      'post1',
      Global.urls.CompanyMenu.deleteCouBasicD1,
      payload
    );
    return normalizeCouBasicBatchResults(res);
  }, options);
};
